import { useEffect, useState } from 'react';
import type { Onboarding } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { TextField } from '../../components/ui/Field.js';
import { useSendPmoRequest } from './api.js';

const DEFAULT_TO = 'pmo@nscatalyst.com';

interface EngagePmoModalProps {
  isOpen: boolean;
  onboarding: Onboarding;
  onClose: () => void;
}

/**
 * Sends the Engage PMO email. The body is composed on the server: all the opportunity +
 * onboarding info (including sales-rep-info notes), links to both records, and a button
 * to the public PM pre-assign form. The step completes when the PMO submits that form.
 */
export function EngagePmoModal({ isOpen, onboarding, onClose }: EngagePmoModalProps): JSX.Element {
  const sendMutation = useSendPmoRequest();
  const resetSend = sendMutation.reset;

  const [to, setTo] = useState(DEFAULT_TO);
  const [cc, setCc] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setTo(DEFAULT_TO);
    setCc('');
    resetSend();
    // Only reset on open / record change. eslint config has no exhaustive-deps rule.
  }, [isOpen, onboarding.id, resetSend]);

  const handleSend = (): void => {
    sendMutation.mutate(
      { id: onboarding.id, input: { to: to.trim(), cc: cc.trim() || null } },
      { onSuccess: () => onClose() },
    );
  };

  const error =
    sendMutation.error instanceof ApiError
      ? sendMutation.error.message
      : sendMutation.error
        ? 'Failed to send the Engage PMO email.'
        : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Engage PMO"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSend} isLoading={sendMutation.isPending} disabled={!to.trim()}>
            Send email
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Emails the PMO everything about <strong>{onboarding.opportunityName}</strong> and this
          onboarding record — including any sales rep info, links to both records, and a button to
          pre-assign a Project Manager. Whoever they choose is recorded on the Engage PMO step and
          auto-assigned when the PSA project is created.
        </p>

        {onboarding.preAssignedPmName ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            Pre-assigned PM: <span className="font-semibold">{onboarding.preAssignedPmName}</span>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} />
          <TextField label="CC (optional)" value={cc} onChange={(e) => setCc(e.target.value)} />
        </div>

        <p className="text-xs text-slate-400">
          The email content is composed automatically from the opportunity and onboarding record.
        </p>

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
